import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { MenuItem, Select, Typography } from '@mui/material';

import { FormattedValue } from '../store/formatted-value';
import { FormattedStatus } from '../store/formatted-status';
import { Group } from '../organisms/group';

import { SwitchBox } from '../molecules/switch-box';
import { ValueBox } from '../molecules/value-box';
import { ChartBox } from '../molecules/chart-box';
import { SliderBox } from '../molecules/slider-box';

import { ElectricalGrid } from '../atoms/icons/electrical-grid';
import { ParentPaper } from '../atoms/parent-paper';
import { ControlBox } from '../atoms/control-box';
import { useSelector } from '../store/utils';
import { HelpButton } from '../atoms/help-button';
import { SpotPricePeriod } from '../../../core/src/shared/enums';
import { PlugSummary, PlugSummary15Min } from '../molecules/plug-summary';
import { PlugPeriodSelect } from '../organisms/plug-period-select';

const PlugPage = () => {
  const [plugIndex, setPlugIndex] = useState(0);

  const plugsCount = useSelector((s) => s.variables.PL_NUMBER);
  const plugNames = useSelector((s) => s.variables.PL_NAME);
  const overflow = useSelector((s) => {
    return s.variables.PV_OVERFLOW_MAX_VALUE > 0 || s.variables.PV_OVERFLOW;
  });
  const plugSpotPricePeriod15M = useSelector(
    (s) =>
      s.variables.PL_SP_PRICE_PERIOD[plugIndex] === SpotPricePeriod['15M'] &&
      s.variables.SP_PRICE_PERIOD === SpotPricePeriod['15M']
  );
  const spPricePeriod = useSelector((s) => s.variables.SP_PRICE_PERIOD);
  const plugSpotEnabled = useSelector((s) => s.variables.PL_SPOT[plugIndex]);
  return (
    <>
      <Group layout="list">
        {plugsCount > 1 && (
          <ControlBox
            title={<FormattedMessage id="plug.plug" defaultMessage="Zásuvka" />}
            fullWidth
            control={
              <Select
                value={plugIndex}
                onChange={(e) => setPlugIndex(e.target.value as number)}
              >
                {Array(plugsCount)
                  .fill(null)
                  .map((_, i) => {
                    return (
                      <MenuItem value={i} key={i}>
                        {plugNames[i]}
                      </MenuItem>
                    );
                  })}
              </Select>
            }
          />
        )}

        <SwitchBox
          title={<FormattedMessage id="plug.plug" defaultMessage="Zásuvka" />}
          switchVariableId={`PL_ON[${plugIndex}]`}
          subtitle={<FormattedStatus type="plug" index={plugIndex} />}
          fullWidth
        />
      </Group>
      <Group
        title={
          <FormattedMessage id="plug.main.stat" defaultMessage="Statistiky" />
        }
        layout="list"
      >
        <ChartBox
          variableId="PL_POWER_GRAPH"
          variableSuffix={plugIndex === 0 ? undefined : String(plugIndex)}
        />
      </Group>
      <Group
        title={
          <FormattedMessage id="plug.main" defaultMessage="Obecné informace" />
        }
        layout="list"
      >
        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="plug.main.consumption"
              defaultMessage="Aktuální spotřeba"
            />
          }
          value={
            <FormattedValue
              variableId={`PL_CONSUMPTIN[${plugIndex}]`}
              opts={{
                style: 'unit',
                unit: 'kilowatt',
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
              }}
            />
          }
          icon={<ElectricalGrid />}
        />
        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="plug.main.power"
              defaultMessage="Celková spotřeba"
            />
          }
          value={
            <FormattedValue
              variableId={`NEW_PL_ENERGY_PRODUCED_TOTAL[${plugIndex}]`}
              opts={{
                style: 'unit',
                unit: 'kilowatt-hour',
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
              }}
            />
          }
          icon={<ElectricalGrid />}
          expanded={
            <Typography variant="body1" sx={{ color: 'text.disabled', mt: 1 }}>
              <FormattedMessage
                id="plug.main.power_today"
                defaultMessage="Dnešní spotřeba:"
              />
              &nbsp;
              <FormattedValue
                variableId={`PL_ENERGY_CONSUMPTIN_TODAY[${plugIndex}]`}
                opts={{
                  style: 'unit',
                  unit: 'kilowatt-hour',
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                }}
              />
              <br />
              <FormattedMessage
                id="plug.main.power_year"
                defaultMessage="Roční spotřeba:"
              />
              &nbsp;
              <FormattedValue
                variableId={`PL_ENERGY_CONSUMPTIN_YEAR[${plugIndex}]`}
                opts={{
                  style: 'unit',
                  unit: 'kilowatt-hour',
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                }}
              />
            </Typography>
          }
        />
      </Group>

      <Group
        title={
          <>
            <FormattedMessage
              id="plug.time"
              defaultMessage="Řízení podle času"
            />
            <HelpButton
              variant="dialog"
              title={
                <FormattedMessage
                  id="plug.time"
                  defaultMessage="Řízení podle času"
                />
              }
              content={
                <FormattedMessage
                  id="plug.time.help"
                  defaultMessage="Spínání při definovaném čase. Lze kombinovat s ostatními podmínkami dle baterie (musí být nainstalována baterie), spotové ceny (musí být povoleno spotové obchodování) a přetoku (musí být povolen přetok do DS). Při kombinaci podmínek se zásuvka zapne, pokud je splněna alespoň jedna z podmínek pro zapnutí a vypne, pokud je splněna alespoň jedna z podmínek pro vypnutí."
                />
              }
            />
          </>
        }
        layout="list"
      >
        <ParentPaper>
          <SwitchBox
            title={
              <FormattedMessage id="plug.time.on" defaultMessage="Aktivní" />
            }
            switchVariableId={`PL_TIME[${plugIndex}]`}
            fullWidth
          />
          <SliderBox
            type="time"
            title={
              <>
                <FormattedMessage id="plug.turn_on" defaultMessage="Zapnout" />
                <HelpButton
                  variant="dialog"
                  title={
                    <FormattedMessage
                      id="plug.time.turn_on"
                      defaultMessage="Zapnout v čase"
                    />
                  }
                  content={
                    <FormattedMessage
                      id="plug.time.turn_on.help"
                      defaultMessage="Čas, ve kterém se zásuvka zapne. Pokud je nastaveno na 12:00, tak se zásuvka zapne přesně v nastavený čas 12:00."
                    />
                  }
                />
              </>
            }
            variableId={`PL_TIME_START[${plugIndex}]`}
            visibility={{ variableId: `PL_TIME[${plugIndex}]` }}
            track={false}
          />

          <SliderBox
            type="time"
            title={
              <>
                <FormattedMessage id="plug.turn_off" defaultMessage="Vypnout" />
                <HelpButton
                  variant="dialog"
                  title={
                    <FormattedMessage
                      id="plug.time.turn_off"
                      defaultMessage="Vypnout v čase"
                    />
                  }
                  content={
                    <FormattedMessage
                      id="plug.time.turn_off.help"
                      defaultMessage="Čas, ve kterém se zásuvka vypne. Pokud je nastaveno na 12:00, tak se zásuvka vypne přesně v nastavený čas 12:00."
                    />
                  }
                />
              </>
            }
            variableId={`PL_TIME_STOP[${plugIndex}]`}
            visibility={{ variableId: `PL_TIME[${plugIndex}]` }}
            track={false}
          />
        </ParentPaper>
      </Group>

      <Group
        title={
          <>
            <FormattedMessage
              id="plug.bat"
              defaultMessage="Řízení podle baterie"
            />
            <HelpButton
              variant="dialog"
              title={
                <FormattedMessage
                  id="plug.bat"
                  defaultMessage="Řízení podle baterie"
                />
              }
              content={
                <FormattedMessage
                  id="plug.bat.help"
                  defaultMessage="Spínání při definované úrovni nabití baterie. Lze kombinovat s podmínkou času. Při kombinaci podmínek se zásuvka zapne, pokud je splněna alespoň jedna z podmínek pro zapnutí a vypne, pokud je splněna alespoň jedna z podmínek pro vypnutí."
                />
              }
            />
          </>
        }
        layout="list"
        visibility={{ variableId: 'PV_BATTERY' }}
      >
        <ParentPaper>
          <SwitchBox
            title={
              <FormattedMessage id="plug.bat.on" defaultMessage="Aktivní" />
            }
            switchVariableId={`PL_BAT[${plugIndex}]`}
            fullWidth
          />
          <SliderBox
            title={
              <>
                <FormattedMessage
                  id="plug.bat.batterySart"
                  defaultMessage="Zapnout při nabití"
                />
                <HelpButton
                  variant="dialog"
                  title={
                    <FormattedMessage
                      id="plug.bat.batterySart"
                      defaultMessage="Zapnout při nabití"
                    />
                  }
                  content={
                    <FormattedMessage
                      id="plug.bat.batterySart.help"
                      defaultMessage="Úroveň nabití baterie, při kterém se zásuvka zapne. Aby k zapnutí došlo, musí dojít k přechodu nabití baterie z nastaveného stavu na vyšší stav. Pokud je hodnota nastavena na 80%, tak zásuvka sepne při nabití baterie ze 80% na 81%."
                    />
                  }
                />
              </>
            }
            subtitle={
              <FormattedMessage
                id="plug.bat.batterySart.subtitle"
                defaultMessage="Nabití baterie při kterém se aktivuje zásuvka"
              />
            }
            visibility={{ variableId: `PL_BAT[${plugIndex}]` }}
            variableId={`PL_BAT_START[${plugIndex}]`}
            opts={{ style: 'percent', maximumFractionDigits: 0 }}
            min={20}
            max={99}
          />
          <SliderBox
            title={
              <>
                <FormattedMessage
                  id="plug.bat.batteryStop"
                  defaultMessage="Vypnout při nabití"
                />
                <HelpButton
                  variant="dialog"
                  title={
                    <FormattedMessage
                      id="plug.bat.batteryStop"
                      defaultMessage="Vypnout při nabití"
                    />
                  }
                  content={
                    <FormattedMessage
                      id="plug.bat.batteryStop.help"
                      defaultMessage="Úroveň nabití baterie, při kterém se zásuvka vypne. Aby k vypnutí došlo, musí dojít k přechodu nabití baterie z nastaveného na nižší stav. Pokud je hodnota nastavena na 80%, tak zásuvka vypne při vybití baterie ze 80% na 79%."
                    />
                  }
                />
              </>
            }
            subtitle={
              <FormattedMessage
                id="plug.bat.batteryStop.subtitle"
                defaultMessage="Nabití baterie při kterém se vypne zásuvka"
              />
            }
            visibility={{ variableId: `PL_BAT[${plugIndex}]` }}
            variableId={`PL_BAT_STOP[${plugIndex}]`}
            opts={{ style: 'percent', maximumFractionDigits: 0 }}
            min={20}
          />
        </ParentPaper>
      </Group>

      <Group
        title={
          <>
            <FormattedMessage
              id="plug.spot"
              defaultMessage="Řízení podle spotové ceny"
            />
            <HelpButton
              variant="dialog"
              title={
                <FormattedMessage
                  id="plug.spot"
                  defaultMessage="Řízení podle spotové ceny"
                />
              }
              content={
                <FormattedMessage
                  id="plug.spot.help"
                  defaultMessage="Spínání při definované spotové ceně. Lze kombinovat s podmínkou času. Při kombinaci podmínek se zásuvka zapne, pokud je splněna alespoň jedna z podmínek pro zapnutí a vypne, pokud je splněna alespoň jedna z podmínek pro vypnutí."
                />
              }
            />
          </>
        }
        layout="list"
        visibility={{ variableId: 'SP_INSTALL' }}
      >
        <ParentPaper>
          <SwitchBox
            title={
              <FormattedMessage id="plug.spot.on" defaultMessage="Aktivní" />
            }
            visibility={{ variableId: 'SP_INSTALL' }}
            switchVariableId={`PL_SPOT[${plugIndex}]`}
            fullWidth
          />
          {plugSpotEnabled && spPricePeriod === SpotPricePeriod['15M'] && (
            <PlugPeriodSelect plugIndex={plugIndex} />
          )}
        </ParentPaper>
        {!plugSpotPricePeriod15M && (
          <>
            <ParentPaper>
              <ControlBox
                title={
                  <FormattedMessage
                    id="plug.spot.period"
                    defaultMessage="Perioda spotové ceny"
                  />
                }
                fullWidth
                subtitle={
                  <FormattedMessage
                    id="plug.spot.period.subtitle"
                    defaultMessage="60 minutové intervaly"
                  />
                }
                visibility={{ variableId: `PL_SPOT[${plugIndex}]` }}
              />
              <PlugSummary plugIndex={plugIndex} />
              <SwitchBox
                title={
                  <>
                    <FormattedMessage
                      id="plug.spot.spotLow"
                      defaultMessage="Aktivovat při nízké ceně"
                    />
                    <HelpButton
                      variant="dialog"
                      title={
                        <FormattedMessage
                          id="plug.spot.spotLow"
                          defaultMessage="Aktivovat při nízké ceně"
                        />
                      }
                      content={
                        <FormattedMessage
                          id="plug.spot.spotLow.help"
                          defaultMessage="Zásuvka se zapne, pokud je cena spotu na nízké úrovni viz záložka Spot."
                        />
                      }
                    />
                  </>
                }
                visibility={{ variableId: `PL_SPOT[${plugIndex}]` }}
                switchVariableId={`PL_SPOT_LOW[${plugIndex}]`}
                fullWidth
              />
              <SwitchBox
                title={
                  <>
                    <FormattedMessage
                      id="plug.spot.spotMi"
                      defaultMessage="Aktivovat při střední ceně"
                    />
                    <HelpButton
                      variant="dialog"
                      title={
                        <FormattedMessage
                          id="plug.spot.spotMi"
                          defaultMessage="Aktivovat při střední ceně"
                        />
                      }
                      content={
                        <FormattedMessage
                          id="plug.spot.spotMi.help"
                          defaultMessage="Zásuvka se zapne, pokud je cena spotu na střední úrovni viz záložka Spot."
                        />
                      }
                    />
                  </>
                }
                visibility={{ variableId: `PL_SPOT[${plugIndex}]` }}
                switchVariableId={`PL_SPOT_MI[${plugIndex}]`}
                fullWidth
              />
              <SwitchBox
                title={
                  <>
                    <FormattedMessage
                      id="plug.spot.spotHi"
                      defaultMessage="Aktivovat při vysoké ceně"
                    />
                    <HelpButton
                      variant="dialog"
                      title={
                        <FormattedMessage
                          id="plug.spot.spotHi"
                          defaultMessage="Aktivovat při vysoké ceně"
                        />
                      }
                      content={
                        <FormattedMessage
                          id="plug.spot.spotHi.help"
                          defaultMessage="Zásuvka se zapne, pokud je cena spotu na vysoké úrovni viz záložka Spot."
                        />
                      }
                    />
                  </>
                }
                visibility={{ variableId: `PL_SPOT[${plugIndex}]` }}
                switchVariableId={`PL_SPOT_HI[${plugIndex}]`}
                fullWidth
              />
              <SwitchBox
                title={
                  <>
                    <FormattedMessage
                      id="plug.spot.spot_neg"
                      defaultMessage="Aktivovat při zákazu přetoků do sítě"
                    />
                    <HelpButton
                      variant="dialog"
                      title={
                        <FormattedMessage
                          id="plug.spot.spot_neg"
                          defaultMessage="Aktivovat při zákazu přetoků do sítě"
                        />
                      }
                      content={
                        <FormattedMessage
                          id="plug.spot.spot_neg.help"
                          defaultMessage="Zásuvka se aktivuje, pokud je zakázán přetok do sítě viz plán spotového obchodování."
                        />
                      }
                    />
                  </>
                }
                visibility={{ variableId: `PL_SPOT[${plugIndex}]` }}
                switchVariableId={`PL_SPOT_NEG[${plugIndex}]`}
                fullWidth
              />
              <SwitchBox
                title={
                  <>
                    <FormattedMessage
                      id="plug.spot.spot_below_price"
                      defaultMessage="Aktivovat při nižší než nastavené ceně"
                    />
                    <HelpButton
                      variant="dialog"
                      title={
                        <FormattedMessage
                          id="plug.spot.spot_below_price"
                          defaultMessage="Aktivovat při nižší než nastavené ceně"
                        />
                      }
                      content={
                        <FormattedMessage
                          id="plug.spot.spot_below_price.help"
                          defaultMessage="Zásuvka se aktivuje, pokud je cena spotu nižší nebo rovna nastavené ceně spotu."
                        />
                      }
                    />
                  </>
                }
                visibility={{ variableId: `PL_SPOT[${plugIndex}]` }}
                switchVariableId={`PL_SPOT_BELOW_PLUG[${plugIndex}]`}
                fullWidth
              />
              <SliderBox
                title={
                  <>
                    <FormattedMessage
                      id="settings.spot.price_below"
                      defaultMessage="Nastavená cena spotu"
                    />
                  </>
                }
                variableId={`SP_PRICE_BELOW_PLUG_VALUE[${plugIndex}]`}
                visibility={{
                  variableIds: [
                    `PL_SPOT_BELOW_PLUG[${plugIndex}]`,
                    `PL_SPOT[${plugIndex}]`,
                  ],
                }}
                subtitle={
                  <FormattedMessage
                    id="plug.spot.spot_below_price.subtitle"
                    defaultMessage="Pokud je cena spotu nižší nebo rovna této hodnotě, dojde k sepnutí. Pokud je vyšší, dojde k rozepnutí."
                  />
                }
                opts={{ style: 'currency' }}
                min={{ CZK: -10, EUR: -0.5 }}
                max={{ CZK: 10, EUR: 0.5 }}
              />
              <SwitchBox
                title={
                  <>
                    <FormattedMessage
                      id="plug.spot.spot_above_price"
                      defaultMessage="Aktivovat při vyšší než nastavené ceně"
                    />
                    <HelpButton
                      variant="dialog"
                      title={
                        <FormattedMessage
                          id="plug.spot.spot_above_price"
                          defaultMessage="Aktivovat při vyšší než nastavené ceně"
                        />
                      }
                      content={
                        <FormattedMessage
                          id="plug.spot.spot_above_price.help"
                          defaultMessage="Zásuvka se aktivuje, pokud je cena spotu vyšší nebo rovna nastavené ceně spotu."
                        />
                      }
                    />
                  </>
                }
                visibility={{ variableId: `PL_SPOT[${plugIndex}]` }}
                switchVariableId={`PL_SPOT_ABOVE_PLUG[${plugIndex}]`}
                fullWidth
              />
              <SliderBox
                title={
                  <>
                    <FormattedMessage
                      id="settings.spot.price_above_price"
                      defaultMessage="Nastavená cena spotu"
                    />
                  </>
                }
                variableId={`SP_PRICE_ABOVE_PLUG_VALUE[${plugIndex}]`}
                visibility={{
                  variableIds: [
                    `PL_SPOT_ABOVE_PLUG[${plugIndex}]`,
                    `PL_SPOT[${plugIndex}]`,
                  ],
                }}
                subtitle={
                  <FormattedMessage
                    id="plug.spot.spot_above_price.subtitle"
                    defaultMessage="Pokud je cena spotu vyšší nebo rovna této hodnotě, dojde k sepnutí. Pokud je nižší, dojde k rozepnutí."
                  />
                }
                opts={{ style: 'currency' }}
                min={{ CZK: -10, EUR: -0.5 }}
                max={{ CZK: 10, EUR: 0.5 }}
              />
            </ParentPaper>
          </>
        )}
        {plugSpotPricePeriod15M && (
          <>
            <ParentPaper>
              <ControlBox
                title={
                  <FormattedMessage
                    id="plug.spot.15m.period"
                    defaultMessage="Perioda spotové ceny"
                  />
                }
                fullWidth
                subtitle={
                  <FormattedMessage
                    id="plug.spot.15m.period.subtitle"
                    defaultMessage="15 minutové intervaly"
                  />
                }
                visibility={{ variableId: `PL_SPOT[${plugIndex}]` }}
              />
              <PlugSummary15Min plugIndex={plugIndex} />
              <SwitchBox
                title={
                  <>
                    <FormattedMessage
                      id="plug.spot.spotLow"
                      defaultMessage="Aktivovat při nízké ceně"
                    />
                    <HelpButton
                      variant="dialog"
                      title={
                        <FormattedMessage
                          id="plug.spot.spotLow"
                          defaultMessage="Aktivovat při nízké ceně"
                        />
                      }
                      content={
                        <FormattedMessage
                          id="plug.spot.spotLow.help"
                          defaultMessage="Zásuvka se zapne, pokud je cena spotu na nízké úrovni viz záložka Spot."
                        />
                      }
                    />
                  </>
                }
                visibility={{ variableId: `PL_SPOT[${plugIndex}]` }}
                switchVariableId={`PL_SPOT_LOW_15MIN[${plugIndex}]`}
                fullWidth
              />
              <SwitchBox
                title={
                  <>
                    <FormattedMessage
                      id="plug.spot.spotMi"
                      defaultMessage="Aktivovat při střední ceně"
                    />
                    <HelpButton
                      variant="dialog"
                      title={
                        <FormattedMessage
                          id="plug.spot.spotMi"
                          defaultMessage="Aktivovat při střední ceně"
                        />
                      }
                      content={
                        <FormattedMessage
                          id="plug.spot.spotMi.help"
                          defaultMessage="Zásuvka se zapne, pokud je cena spotu na střední úrovni viz záložka Spot."
                        />
                      }
                    />
                  </>
                }
                visibility={{ variableId: `PL_SPOT[${plugIndex}]` }}
                switchVariableId={`PL_SPOT_MI_15MIN[${plugIndex}]`}
                fullWidth
              />
              <SwitchBox
                title={
                  <>
                    <FormattedMessage
                      id="plug.spot.spotHi"
                      defaultMessage="Aktivovat při vysoké ceně"
                    />
                    <HelpButton
                      variant="dialog"
                      title={
                        <FormattedMessage
                          id="plug.spot.spotHi"
                          defaultMessage="Aktivovat při vysoké ceně"
                        />
                      }
                      content={
                        <FormattedMessage
                          id="plug.spot.spotHi.help"
                          defaultMessage="Zásuvka se zapne, pokud je cena spotu na vysoké úrovni viz záložka Spot."
                        />
                      }
                    />
                  </>
                }
                visibility={{ variableId: `PL_SPOT[${plugIndex}]` }}
                switchVariableId={`PL_SPOT_HI_15MIN[${plugIndex}]`}
                fullWidth
              />
              <SwitchBox
                title={
                  <>
                    <FormattedMessage
                      id="plug.spot.spot_neg"
                      defaultMessage="Aktivovat při zákazu přetoků do sítě"
                    />
                    <HelpButton
                      variant="dialog"
                      title={
                        <FormattedMessage
                          id="plug.spot.spot_neg"
                          defaultMessage="Aktivovat při zákazu přetoků do sítě"
                        />
                      }
                      content={
                        <FormattedMessage
                          id="plug.spot.spot_neg.help"
                          defaultMessage="Zásuvka se aktivuje, pokud je zakázán přetok do sítě viz plán spotového obchodování."
                        />
                      }
                    />
                  </>
                }
                visibility={{ variableId: `PL_SPOT[${plugIndex}]` }}
                switchVariableId={`PL_SPOT_NEG_15MIN[${plugIndex}]`}
                fullWidth
              />
              <SwitchBox
                title={
                  <>
                    <FormattedMessage
                      id="plug.spot.spot_below_price"
                      defaultMessage="Aktivovat při nižší než nastavené ceně"
                    />
                    <HelpButton
                      variant="dialog"
                      title={
                        <FormattedMessage
                          id="plug.spot.spot_below_price"
                          defaultMessage="Aktivovat při nižší než nastavené ceně"
                        />
                      }
                      content={
                        <FormattedMessage
                          id="plug.spot.spot_below_price.help"
                          defaultMessage="Zásuvka se aktivuje, pokud je cena spotu nižší nebo rovna nastavené ceně spotu."
                        />
                      }
                    />
                  </>
                }
                visibility={{ variableId: `PL_SPOT[${plugIndex}]` }}
                switchVariableId={`PL_SPOT_BELOW_PLUG_15MIN[${plugIndex}]`}
                fullWidth
              />
              <SliderBox
                title={
                  <>
                    <FormattedMessage
                      id="settings.spot.price_below"
                      defaultMessage="Nastavená cena spotu"
                    />
                  </>
                }
                variableId={`SP_PRICE_BELOW_PLUG_15MIN_VALUE[${plugIndex}]`}
                visibility={{
                  variableIds: [
                    `PL_SPOT_BELOW_PLUG_15MIN[${plugIndex}]`,
                    `PL_SPOT[${plugIndex}]`,
                  ],
                }}
                subtitle={
                  <FormattedMessage
                    id="plug.spot.spot_below_price.subtitle"
                    defaultMessage="Pokud je cena spotu nižší nebo rovna této hodnotě, dojde k sepnutí. Pokud je vyšší, dojde k rozepnutí."
                  />
                }
                opts={{ style: 'currency' }}
                min={{ CZK: -10, EUR: -0.5 }}
                max={{ CZK: 10, EUR: 0.5 }}
              />
              <SwitchBox
                title={
                  <>
                    <FormattedMessage
                      id="plug.spot.spot_above_price"
                      defaultMessage="Aktivovat při vyšší než nastavené ceně"
                    />
                    <HelpButton
                      variant="dialog"
                      title={
                        <FormattedMessage
                          id="plug.spot.spot_above_price"
                          defaultMessage="Aktivovat při vyšší než nastavené ceně"
                        />
                      }
                      content={
                        <FormattedMessage
                          id="plug.spot.spot_above_price.help"
                          defaultMessage="Zásuvka se aktivuje, pokud je cena spotu vyšší nebo rovna nastavené ceně spotu."
                        />
                      }
                    />
                  </>
                }
                visibility={{ variableId: `PL_SPOT[${plugIndex}]` }}
                switchVariableId={`PL_SPOT_ABOVE_PLUG_15MIN[${plugIndex}]`}
                fullWidth
              />
              <SliderBox
                title={
                  <>
                    <FormattedMessage
                      id="settings.spot.price_above_price"
                      defaultMessage="Nastavená cena spotu"
                    />
                  </>
                }
                variableId={`SP_PRICE_ABOVE_PLUG_15MIN_VALUE[${plugIndex}]`}
                visibility={{
                  variableIds: [
                    `PL_SPOT_ABOVE_PLUG_15MIN[${plugIndex}]`,
                    `PL_SPOT[${plugIndex}]`,
                  ],
                }}
                subtitle={
                  <FormattedMessage
                    id="plug.spot.spot_above_price.subtitle"
                    defaultMessage="Pokud je cena spotu vyšší nebo rovna této hodnotě, dojde k sepnutí. Pokud je nižší, dojde k rozepnutí."
                  />
                }
                opts={{ style: 'currency' }}
                min={{ CZK: -10, EUR: -0.5 }}
                max={{ CZK: 10, EUR: 0.5 }}
              />
            </ParentPaper>
          </>
        )}
      </Group>

      {overflow && (
        <Group
          title={
            <>
              <FormattedMessage
                id="plug.overflow"
                defaultMessage="Řízení podle přetoku"
              />
              <HelpButton
                variant="dialog"
                title={
                  <FormattedMessage
                    id="plug.overflow"
                    defaultMessage="Řízení podle přetoku"
                  />
                }
                content={
                  <FormattedMessage
                    id="plug.overflow.help"
                    defaultMessage="Zapínání zásuvky při definovaném přetoku do distribuční sítě. Lze kombinovat s podmínkou času. Při kombinaci podmínek se zásuvka zapne, pokud je splněna alespoň jedna z podmínek pro zapnutí a vypne, pokud je splněna alespoň jedna z podmínek pro vypnutí."
                  />
                }
              />
            </>
          }
          layout="list"
        >
          <ParentPaper>
            <SwitchBox
              title={
                <FormattedMessage
                  id="plug.overflow.on"
                  defaultMessage="Aktivní"
                />
              }
              switchVariableId={`PL_OVERFLOW[${plugIndex}]`}
              fullWidth
            />
            <ValueBox
              type="row"
              title={
                <FormattedMessage
                  id="plug.overflow.consumption"
                  defaultMessage="Nastavení odpovídá spotřebiči s příkonem"
                />
              }
              visibility={{ variableId: `PL_OVERFLOW[${plugIndex}]` }}
              value={
                <FormattedValue
                  variableId={`PL_OVERFLOW_CONSUMPTION[${plugIndex}]`}
                  opts={{
                    style: 'unit',
                    unit: 'kilowatt',
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2,
                  }}
                />
              }
              icon={<ElectricalGrid />}
            />
            <SliderBox
              title={
                <>
                  <FormattedMessage
                    id="plug.overflow.startPower"
                    defaultMessage="Zapnout při odběru ze sítě"
                  />
                  <HelpButton
                    variant="dialog"
                    title={
                      <FormattedMessage
                        id="plug.overflow.startPower"
                        defaultMessage="Zapnout při odběru ze sítě"
                      />
                    }
                    content={
                      <FormattedMessage
                        id="plug.overflow.startPower.help"
                        defaultMessage="Zásuvka se zapne, pokud je přetok v nastavené výši po definovaný interval zpoždění. Pokud je hodnota nastavena na - 1500W a zpoždění zapnutí je 5 minut, tak zásuvka sepne při přetoku do sítě ve výši alespoň 1500W po dobu 5 minut."
                      />
                    }
                  />
                </>
              }
              subtitle={
                <FormattedMessage
                  id="plug.overflow.startPower.subtitle"
                  defaultMessage="Záporná hodnota je přetok, kladná hodnota je odběr"
                />
              }
              variableId={`PL_OVERFLOW_START_POWER[${plugIndex}]`}
              visibility={{ variableId: `PL_OVERFLOW[${plugIndex}]` }}
              opts={{
                style: 'unit',
                unit: 'kilowatt',
                maximumFractionDigits: 1,
              }}
              step={0.1}
              min={-15}
              max={0}
            />
            <SliderBox
              title={
                <>
                  <FormattedMessage
                    id="plug.overflow.stopPower"
                    defaultMessage="Vypnout při odběru ze sítě"
                  />
                  <HelpButton
                    variant="dialog"
                    title={
                      <FormattedMessage
                        id="plug.overflow.stopPower"
                        defaultMessage="Vypnout při odběru ze sítě"
                      />
                    }
                    content={
                      <FormattedMessage
                        id="plug.overflow.stopPower.help"
                        defaultMessage="Zásuvka se vypne, pokud je odběr vyšší než nastavený po definovaný interval zpoždění. Pokud je hodnota nastavena na 100W a zpoždění vypnutí je 5 minut, tak zásuvka vypne při odběru ze sítě ve výši minimálně 100W po dobu 5 minut."
                      />
                    }
                  />
                </>
              }
              subtitle={
                <FormattedMessage
                  id="plug.overflow.stopPower.subtitle"
                  defaultMessage="Záporná hodnota je přetok, kladná hodnota je odběr"
                />
              }
              variableId={`PL_OVERFLOW_STOP_POWER[${plugIndex}]`}
              visibility={{ variableId: `PL_OVERFLOW[${plugIndex}]` }}
              opts={{
                style: 'unit',
                unit: 'kilowatt',
                maximumFractionDigits: 1,
              }}
              step={0.1}
              min={-10}
              max={5}
            />
            <SliderBox
              type="time"
              title={
                <>
                  <FormattedMessage
                    id="plug.overflow.time"
                    defaultMessage="Zpoždění zapnutí a vypnutí"
                  />
                  <HelpButton
                    variant="dialog"
                    title={
                      <FormattedMessage
                        id="plug.overflow.time"
                        defaultMessage="Zpoždění zapnutí a vypnutí"
                      />
                    }
                    content={
                      <FormattedMessage
                        id="plug.overflow.time.help"
                        defaultMessage="Tento parametr slouží pro definici zpoždění zapnutí a vypnutí zásuvky při definovaných přetocích/odběrech. Vhodné pro nastavení priority a eliminace častého spínání zásuvky. Zadávejte ve formátu HH:MM."
                      />
                    }
                  />
                </>
              }
              variableId={`PL_OVERFLOW_TON[${plugIndex}]`}
              visibility={{ variableId: `PL_OVERFLOW[${plugIndex}]` }}
              track={false}
              max={3600}
              step={60}
            />
          </ParentPaper>
        </Group>
      )}
    </>
  );
};

export default PlugPage;
